import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Lock, ChevronRight, Trophy, X, CreditCard, Smartphone, Banknote, Star, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const TITHELY_BASE_URL = process.env.REACT_APP_TITHELY_URL || 'https://give.tithe.ly/?formId=65d77a19-6865-11ee-90fc-1260ab546d11&locationId=d916ac2c-d526-4a17-b05d-5e5193feacf7&fundId=9f121d40-a037-48f0-bdfd-79454e38014c';
const TITHELY_ANGELO_URL = process.env.REACT_APP_TITHELY_ANGELO_URL || 'https://give.tithe.ly/?formId=65d77a19-6865-11ee-90fc-1260ab546d11&locationId=d916ac2c-d526-4a17-b05d-5e5193feacf7&fundId=f1c8b6d9-1fe3-4976-9796-6e00a3129abf';
const VENMO_URL = process.env.REACT_APP_VENMO_URL;
const ZELLE_URL = process.env.REACT_APP_ZELLE_URL;

const DonatePage = () => {
  const [donationType, setDonationType] = useState('monthly');
  const [customAmount, setCustomAmount] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMonthlyReminder, setShowMonthlyReminder] = useState(false);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [pendingType, setPendingType] = useState('monthly');
  const [pendingFund, setPendingFund] = useState('main');
  const [angeloAmount, setAngeloAmount] = useState('');
  const { language } = useLanguage();

  const impactBullets = language === 'en'
    ? [
        'Daily meals for children in our care',
        'Hygiene essentials, clothing & school supplies',
        'Safe shelter & full Rescue Center operations',
        'Education, mentoring & spiritual guidance',
        'Weekly outreach to vulnerable families',
      ]
    : [
        'Comidas diarias para los niños a nuestro cuidado',
        'Artículos de higiene, ropa y útiles escolares',
        'Refugio seguro y operaciones completas del Centro de Rescate',
        'Educación, mentoría y guía espiritual',
        'Alcance semanal a familias vulnerables',
      ];

  const handleMonthlyDonate = () => {
    const amount = customAmount ? parseFloat(customAmount) : 0;
    if (!amount || amount <= 0 || isNaN(amount)) {
      alert(language === 'en' ? 'Please enter a donation amount' : 'Por favor ingrese un monto de donación');
      return;
    }
    setPendingAmount(amount);
    setPendingType(donationType);
    setPendingFund('main');
    setShowPaymentModal(true);
  };

  const handleAngeloDonate = (type) => {
    const amount = angeloAmount ? parseFloat(angeloAmount) : 0;
    if (!amount || amount <= 0 || isNaN(amount)) return;
    setPendingAmount(amount);
    setPendingType(type);
    setPendingFund('angelo');
    setShowPaymentModal(true);
  };

  const handlePaymentChoice = (method) => {
    setShowPaymentModal(false);
    if (method === 'credit') {
      const amountInCents = Math.round(pendingAmount * 100);
      const baseUrl = pendingFund === 'angelo' ? TITHELY_ANGELO_URL : TITHELY_BASE_URL;
      // Tithe.ly 2.0 form cannot pre-select a recurring frequency via URL (verified
      // against their docs, live tests, and their form bundle). frequency=one-time
      // hides the recurring options for a clean one-time checkout; monthly donors
      // get a quick reminder step telling them to tap "Monthly" on the form.
      if (pendingType === 'monthly') {
        setShowMonthlyReminder(true);
      } else {
        window.open(`${baseUrl}&amount=${amountInCents}&frequency=one-time`, '_blank');
      }
    } else if (method === 'venmo') {
      window.open(VENMO_URL, '_blank');
    } else if (method === 'zelle') {
      window.open(ZELLE_URL, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="donate-page">

      {/* 2️⃣ URGENCY SECTION - Full Picture with Centered Text */}
      <section className="relative bg-black overflow-hidden pt-16 md:pt-20">
        <div className="relative">
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_bd8ea268-ae82-484d-8d3e-447aa998fd80/artifacts/32u79hlb_IMG_6106.webp"
            alt="Safe House children together"
            className="w-full h-auto block"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          <div className="absolute inset-0 flex items-center justify-center px-1 pb-6 md:pb-10 text-center">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="leading-relaxed"
            >
              <span className={`text-white italic font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] ${language === 'en' ? 'whitespace-nowrap text-[12.5px] tracking-tighter' : 'text-xs'} sm:text-base md:text-xl`}>
                “{t('donate.quoteInspirational', language)}
              </span>
              <br />
              <span className="whitespace-nowrap text-colombia-yellow font-bold not-italic text-[13px] tracking-tight sm:text-base md:text-xl drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
                {t('donate.quoteTriumphs', language)}”{' '}<Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-colombia-yellow inline-block align-middle -mt-0.5" />
              </span>
            </motion.p>
          </div>
        </div>
      </section>

      {/* 3️⃣ SIMPLIFIED MONTHLY GIVING CARD */}
      <section id="monthly-give" className="pt-8 md:pt-12 pb-6 md:pb-8 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
        <div className="max-w-2xl mx-auto px-4">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-colombia-yellow/20 border border-colombia-yellow/40 rounded-full mb-4">
              <Star className="w-4 h-4 text-amber-600" fill="currentColor" />
              <span className="text-xs font-bold tracking-widest uppercase text-amber-800">
                {language === 'en' ? 'Make a Donation' : 'Haz una Donación'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              {donationType === 'monthly'
                ? (language === 'en' ? 'Become a Monthly Sponsor' : 'Conviértete en Patrocinador Mensual')
                : (language === 'en' ? 'Give a One-Time Gift' : 'Da una Donación Única')}
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              {donationType === 'monthly'
                ? (language === 'en'
                    ? 'Your recurring monthly gift provides consistent support to children who need it most.'
                    : 'Tu donación mensual recurrente proporciona apoyo constante a los niños que más lo necesitan.')
                : (language === 'en'
                    ? 'A single gift, given from the heart, that immediately impacts the children we serve.'
                    : 'Un regalo único, dado desde el corazón, que impacta inmediatamente a los niños a los que servimos.')}
            </p>
          </div>

          {/* Monthly / One-Time Toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-white rounded-full p-1.5 shadow-md border border-gray-200" data-testid="donation-type-toggle">
              <button
                onClick={() => setDonationType('monthly')}
                className={`px-5 md:px-7 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  donationType === 'monthly'
                    ? 'bg-colombia-yellow text-gray-900 shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="toggle-monthly"
              >
                <Star className="w-3.5 h-3.5" fill={donationType === 'monthly' ? 'currentColor' : 'none'} />
                {language === 'en' ? 'Monthly' : 'Mensual'}
              </button>
              <button
                onClick={() => setDonationType('one-time')}
                className={`px-5 md:px-7 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                  donationType === 'one-time'
                    ? 'bg-gray-900 text-white shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="toggle-onetime"
              >
                {language === 'en' ? 'One-Time Donation' : 'Donación Única'}
              </button>
            </div>
          </div>

          {/* Impact card */}
          <div className="bg-white rounded-3xl border-2 border-colombia-yellow/30 shadow-xl overflow-hidden">

            {/* Impact bullets */}
            <div className="p-6 md:p-8 bg-gradient-to-b from-white to-amber-50/40">
              <p className="text-sm font-bold tracking-wide uppercase text-amber-700 mb-4 text-center">
                {donationType === 'monthly'
                  ? (language === 'en' ? 'Your Monthly Gift Provides' : 'Tu Donación Mensual Proporciona')
                  : (language === 'en' ? 'Your Gift Provides' : 'Tu Donación Proporciona')}
              </p>
              <ul className="space-y-3" data-testid="impact-bullets">
                {impactBullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-0.5 w-6 h-6 rounded-full bg-colombia-yellow/25 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-amber-700" strokeWidth={3} />
                    </div>
                    <span className="text-gray-800 text-base md:text-lg font-medium leading-snug">
                      {bullet}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Amount + CTA */}
            <div className="p-6 md:p-8 border-t-2 border-dashed border-amber-200">
              <label className="block text-sm font-bold text-gray-700 mb-3 text-center uppercase tracking-wide">
                {donationType === 'monthly'
                  ? (language === 'en' ? 'Choose Your Monthly Amount' : 'Elige Tu Monto Mensual')
                  : (language === 'en' ? 'Choose Your Gift Amount' : 'Elige el Monto de Tu Donación')}
              </label>
              <div className="flex items-center gap-3 mb-5 bg-gray-50 rounded-2xl border-2 border-gray-200 focus-within:border-colombia-yellow px-5 py-4 transition-colors">
                <span className="text-2xl md:text-3xl font-bold text-gray-400">$</span>
                <input
                  type="number"
                  min="1"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={language === 'en' ? 'Amount' : 'Monto'}
                  className="flex-1 text-2xl md:text-3xl font-bold text-gray-900 bg-transparent border-none outline-none placeholder:text-gray-300 min-w-0 w-0"
                  data-testid="custom-amount-input"
                />
                {donationType === 'monthly' && (
                  <span className="text-gray-500 font-semibold text-base md:text-lg whitespace-nowrap">
                    {language === 'en' ? '/ month' : '/ mes'}
                  </span>
                )}
              </div>
              <button
                onClick={handleMonthlyDonate}
                className={`w-full py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 ${
                  donationType === 'monthly'
                    ? 'bg-colombia-yellow text-gray-900 hover:bg-yellow-400'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
                data-testid="donate-btn"
              >
                <Heart className="w-5 h-5" fill="currentColor" />
                {donationType === 'monthly'
                  ? t('donate.becomeMonthly', language)
                  : (language === 'en' ? 'Send Gift' : 'Enviar Regalo')}
                <ChevronRight className="w-5 h-5" />
              </button>
              <p className="text-center text-sm text-gray-500 mt-4 flex items-center justify-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-green-600" />
                {t('donate.secureRedirect', language)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OR Divider */}
      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 pt-0 pb-6">
        <div className="max-w-xl mx-auto px-4 flex items-center gap-4">
          <div className="flex-1 h-px bg-gray-300"></div>
          <span className="text-lg font-bold text-gray-400 tracking-widest">{language === 'en' ? 'OR' : 'O'}</span>
          <div className="flex-1 h-px bg-gray-300"></div>
        </div>
      </div>

      {/* ANGELO'S PERSONAL SPONSORSHIP */}
      <section className="pt-2 pb-10 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50" data-testid="angelo-sponsorship-section">
        <div className="max-w-xl mx-auto px-4">
          <div className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-sm text-center">
            <h3 className="text-sm font-bold text-gray-800 mb-1 text-center tracking-wide uppercase">
              {language === 'en' ? "Angelo's Personal Sponsorship" : 'Patrocinio Personal de Angelo'}
            </h3>
            <p className="text-xs text-gray-500 text-center mb-4">
              {language === 'en'
                ? 'If God puts it on your heart to personally support founder Angelo Gennaro, you can do so here.'
                : 'Si Dios pone en tu corazón apoyar personalmente al fundador Angelo Gennaro, puedes hacerlo aquí.'}
            </p>
            <div className="flex items-center gap-3 mb-4 max-w-xs mx-auto">
              <span className="text-xs text-gray-500">$</span>
              <input
                type="number"
                min="1"
                placeholder={language === 'en' ? 'Enter amount' : 'Ingrese monto'}
                value={angeloAmount}
                onChange={(e) => setAngeloAmount(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-colombia-yellow"
                data-testid="angelo-amount-input"
              />
            </div>
            <div className="flex gap-2 max-w-xs mx-auto">
              <button
                onClick={() => handleAngeloDonate('one-time')}
                className="flex-1 text-xs font-semibold py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="angelo-one-time-btn"
              >
                {language === 'en' ? 'One-Time Gift' : 'Donación Única'}
              </button>
              <button
                onClick={() => handleAngeloDonate('monthly')}
                className="flex-1 text-xs font-semibold py-2.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                data-testid="angelo-monthly-btn"
              >
                {language === 'en' ? 'Monthly Sponsorship' : 'Patrocinio Mensual'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Give With Confidence */}
      <section className="py-12 md:py-16 bg-gray-50 border-y border-gray-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-green-600" />
            <h3 className="text-xl font-bold text-gray-900">{t('donate.giveWithConfidence', language)}</h3>
          </div>
          <p className="text-gray-600 mb-4">
            {t('donate.irsRecognized', language)} <strong>{t('donate.taxExempt', language)}</strong><br />
            {t('donate.taxDeductible', language)}
          </p>
          <p className="text-sm text-gray-500 mt-4">
            {t('donate.hundredPercent', language)}
          </p>
        </div>
      </section>

      {/* Closing Verse over Child Photo */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="closing-verse-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1920&q=80"
            alt="Child's hopeful eyes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-2xl md:text-4xl lg:text-5xl text-white font-semibold italic leading-snug"
          >
            "{language === 'en' ? 'Take up the cause in helping the orphans & fatherless.' : 'Defiende la causa de los huérfanos y desamparados.'}"
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-6 text-lg md:text-xl text-colombia-yellow font-bold tracking-wider"
          >
            — {language === 'en' ? 'Isaiah 1:17' : 'Isaías 1:17'}
          </motion.p>
        </div>
      </section>

      {/* Payment Method Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPaymentModal(false)}
            data-testid="payment-modal-overlay"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
              data-testid="payment-modal"
            >
              <div className="relative bg-gradient-to-br from-sh-dark to-gray-900 px-6 pt-6 pb-8 text-center">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
                  data-testid="payment-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
                <Heart className="w-10 h-10 text-colombia-yellow mx-auto mb-3" fill="currentColor" />
                <h3 className="text-xl font-bold text-white mb-1">
                  {t('payment.chooseHow', language)}
                </h3>
                <div className="inline-flex items-baseline gap-1 mt-2">
                  <span className="text-3xl font-black text-colombia-yellow">${pendingAmount.toLocaleString()}</span>
                  {pendingType === 'monthly' && (
                    <span className="text-sm font-medium text-colombia-yellow/70">{t('payment.month', language)}</span>
                  )}
                </div>
              </div>

              <div className="p-5 space-y-3">
                <button
                  onClick={() => handlePaymentChoice('credit')}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 bg-gray-50 hover:border-sh-primary hover:bg-blue-50/40 transition-all group text-left"
                  data-testid="payment-credit-card"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-base">{t('payment.creditCard', language)}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1.5">{t('payment.securePayment', language)} <Lock className="w-4 h-4 text-green-600" /></div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-sh-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>

                <button
                  onClick={() => handlePaymentChoice('zelle')}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 bg-gray-50 hover:border-purple-500 hover:bg-purple-50/40 transition-all group text-left"
                  data-testid="payment-zelle"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                    <Banknote className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-base">Zelle</div>
                    <div className="text-sm text-gray-500">{t('payment.directBank', language)}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-purple-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>

                <button
                  onClick={() => handlePaymentChoice('venmo')}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 bg-gray-50 hover:border-sky-500 hover:bg-sky-50/40 transition-all group text-left"
                  data-testid="payment-venmo"
                >
                  <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-base">Venmo</div>
                    <div className="text-sm text-gray-500">{t('payment.payWithVenmo', language)}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-sky-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              </div>

              <div className="px-5 pb-5">
                <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 pt-2 border-t border-gray-100">
                  <Lock className="w-3 h-3" />
                  <span>{t('payment.allSecure', language)}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Monthly Reminder Overlay — Tithe.ly's 2.0 form cannot pre-select "Monthly" via URL
          (verified against their docs + live tests). Fund + amount ARE prefilled; this step
          tells the monthly donor to tap "Monthly" on the form before continuing. */}
      <AnimatePresence>
        {showMonthlyReminder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowMonthlyReminder(false)}
            data-testid="monthly-reminder-overlay"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden text-center"
              data-testid="monthly-reminder-modal"
            >
              <div className="bg-gradient-to-br from-amber-400 to-amber-500 px-6 pt-6 pb-5">
                <Star className="w-10 h-10 text-white mx-auto mb-2" fill="currentColor" />
                <h3 className="text-xl font-black text-white">
                  {language === 'en' ? 'Monthly Partnership' : 'Asociación Mensual'}
                </h3>
              </div>
              <div className="px-6 py-5">
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  {language === 'en'
                    ? 'The giving form will open with your fund and amount already filled in. One quick step: select "Monthly" as the giving frequency to activate your recurring gift of'
                    : 'El formulario se abrirá con su fondo y monto ya ingresados. Un paso rápido: seleccione "Monthly" como frecuencia para activar su donación recurrente de'}
                  <span className="font-bold text-gray-900"> ${pendingAmount.toLocaleString()}{language === 'en' ? '/mo' : '/mes'}</span>.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="text-amber-800 text-xs font-semibold">
                    {language === 'en'
                      ? 'Look for the frequency options (One Time, Weekly, Monthly...) and tap "Monthly"'
                      : 'Busque las opciones de frecuencia (One Time, Weekly, Monthly...) y toque "Monthly"'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    const amountInCents = Math.round(pendingAmount * 100);
                    const baseUrl = pendingFund === 'angelo' ? TITHELY_ANGELO_URL : TITHELY_BASE_URL;
                    window.open(`${baseUrl}&amount=${amountInCents}`, '_blank');
                    setShowMonthlyReminder(false);
                  }}
                  className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="monthly-reminder-continue"
                >
                  {language === 'en' ? 'Continue to Tithe.ly' : 'Continuar a Tithe.ly'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default DonatePage;
